/**
 * Sistema de CAPTCHA para Prevenção de Bots
 * Implementação com hCaptcha (privacy-focused, GDPR compliant)
 */

import { createContext, useContext, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { logger } from '@/lib/logger'

interface CaptchaContextType {
  isCaptchaRequired: boolean
  checkCaptchaRequired: () => Promise<boolean>
  verifyCaptcha: (token: string) => Promise<boolean>
  resetCaptcha: () => void
}

const CaptchaContext = createContext<CaptchaContextType | null>(null)

// Configurações
const CAPTCHA_CONFIG = {
  // Intervalo de tempo (minutos)
  TIME_INTERVAL: 40,
  
  // Número de rolls antes de exigir CAPTCHA
  ROLL_COUNT_TRIGGER: 3,
  
  // Score de suspeita que dispara CAPTCHA (0-100)
  SUSPICION_THRESHOLD: 30,
  
  // Chave do site hCaptcha (obrigatória no .env)
  SITE_KEY: import.meta.env.VITE_HCAPTCHA_SITE_KEY,
  
  // 🎯 Nível de dificuldade (pode ser alterado via .env)
  // Opções: 'easy' | 'moderate' | 'difficult'
  DIFFICULTY: (import.meta.env.VITE_HCAPTCHA_DIFFICULTY || 'moderate') as 'easy' | 'moderate' | 'difficult'
}

export function CaptchaProvider({ children }: { children: React.ReactNode }) {
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false)

  /**
   * Verificar se CAPTCHA é necessário
   */
  const checkCaptchaRequired = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        logger.dev('❌ checkCaptchaRequired: Usuário não autenticado')
        return false
      }

      logger.dev('🔍 Verificando necessidade de CAPTCHA para:', user.id)

      // Chamar função do Supabase que verifica necessidade de CAPTCHA
      const { data, error } = await supabase.rpc('check_captcha_required', {
        p_user_id: user.id
      })

      logger.dev('📊 Resposta do check_captcha_required:', { data, error })

      if (error) {
        logger.error('❌ Erro ao verificar CAPTCHA:', error)
        return false
      }

      // A função SQL retorna um ARRAY, precisamos acessar o primeiro elemento
      const result = Array.isArray(data) ? data[0] : data
      const required = result?.captcha_required || false
      
      logger.dev('✅ CAPTCHA necessário?', required)
      logger.dev('📋 Detalhes:', result)
      
      setIsCaptchaRequired(required)
      return required

    } catch (error) {
      logger.error('💥 Exceção ao verificar necessidade de CAPTCHA:', error)
      return false
    }
  }, [])

  /**
   * Verificar token do CAPTCHA no backend
   */
  const verifyCaptcha = useCallback(async (token: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        logger.dev('❌ verifyCaptcha: Usuário não autenticado')
        return false
      }

      logger.dev('🔍 Verificando token CAPTCHA para:', user.id)

      // Enviar token para validação no backend
      const { data, error } = await supabase.rpc('verify_captcha', {
        p_user_id: user.id,
        p_captcha_token: token
      })

      logger.dev('📊 Resposta do verify_captcha:', { data, error })

      if (error) {
        logger.error('❌ Erro ao verificar token CAPTCHA:', error)
        return false
      }

      // A função SQL retorna um ARRAY, precisamos acessar o primeiro elemento
      const result = Array.isArray(data) ? data[0] : data
      const isValid = result?.is_valid || false
      
      logger.dev('✅ Token válido?', isValid)
      
      if (isValid) {
        setIsCaptchaRequired(false)
        logger.dev('🎉 CAPTCHA verificado com sucesso!')
      }

      return isValid

    } catch (error) {
      logger.error('💥 Exceção ao verificar token CAPTCHA:', error)
      return false
    }
  }, [])

  /**
   * Resetar estado do CAPTCHA
   */
  const resetCaptcha = useCallback(() => {
    setIsCaptchaRequired(false)
  }, [])

  return (
    <CaptchaContext.Provider
      value={{
        isCaptchaRequired,
        checkCaptchaRequired,
        verifyCaptcha,
        resetCaptcha
      }}
    >
      {children}
    </CaptchaContext.Provider>
  )
}

/**
 * Hook para usar o sistema de CAPTCHA
 */
export function useCaptcha() {
  const context = useContext(CaptchaContext)
  if (!context) {
    throw new Error('useCaptcha deve ser usado dentro de CaptchaProvider')
  }
  return context
}

/**
 * Configuração do hCaptcha
 */
export const HCAPTCHA_SITE_KEY = CAPTCHA_CONFIG.SITE_KEY
export const HCAPTCHA_DIFFICULTY = CAPTCHA_CONFIG.DIFFICULTY

